import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import { validationContext } from 'validx'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { DocumentType } from '@/features/documents/DocumentType'
import {
  type CreateInventoryItemParams,
  type InventoryStore,
} from '@/features/inventory/InventoryStore'
import { InventoryItemDialogViewModelBase } from '@/features/inventory/admin/inventory-item-dialog/InventoryItemDialogViewModelBase'
import {
  type WorkspaceSelection,
  type WorkspaceSelectorViewModel,
} from '@/features/inventory/admin/WorkspaceSelectorViewModel'
import { type RefreshInventoryItems } from '@/features/inventory/admin/InventorySearchViewModel'

/**
 * Adds a service item to a workspace's inventory.
 */
export class CreateWorkspaceInventoryItemDialogViewModel
  extends InventoryItemDialogViewModelBase
  implements WorkspaceSelection
{
  /**
   * The selected workspace
   */
  @observable accessor workspace: Workspace | undefined = Workspace.none

  /**
   * Validations for the workspace
   */
  readonly workspaceValidation = validationContext(this, {
    workspace: [(opts) => !!opts.value || 'Workspace is required'],
  })

  constructor(
    private readonly _inventoryStore: InventoryStore,
    private readonly _refreshItems: RefreshInventoryItems,
    private readonly _getSelectedWorkspace: () => Workspace | undefined,
    readonly workspaceSelectorVm: WorkspaceSelectorViewModel,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    getDocumentTypes: () => DocumentType[],
  ) {
    super(getDocumentTypes)
  }

  /**
   * Set the workspace for the new item
   */
  @action.bound
  setWorkspace(workspace?: Workspace) {
    this.workspace = workspace
  }

  /**
   * Shows the dialog without resetting what was entered before.
   */
  @action.bound
  show() {
    this.workspace = this._getSelectedWorkspace()
    this.modal.show()
  }

  @action.bound
  open() {
    this.reset()
    this.show()
  }

  /**
   * Creates the inventory item
   */
  submit = task.resolved(async () => {
    if (
      !this.validate() ||
      !this.workspaceValidation.reset().validate().isValid
    ) {
      return
    }

    const payload: CreateInventoryItemParams = {
      name: this.name,
      description: this.description,
      courtesyAmount: this.courtesyQuantity,
      pricing: this.pricingVm.toValues(),
      workspace: this.workspace?.id,
      supportingItems: this.supportingItems,
      hideFromPro: this.hideFromPro,
      typeSpecification: this.getInventoryItemTypeSpecification(),
    }

    await this._inventoryStore
      .createInventoryItem(payload)
      .catch(this._createAlertErrorHandler('Adding the item failed'))
    await this._refreshItems(this.workspace ?? Workspace.none)

    this.validation.reset()

    this._newAlert()
      .withState('success')
      .saying('Success', 'Item added.')
      .autoDismiss()
      .show()

    this.modal.close()
  })
}
